import { Row } from 'antd';
import { Chart, ArcElement, Legend, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IGetSummaryCategoryChart } from 'features/report/services/types/reportStaff';
import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
Chart.register(ArcElement);

interface IncomeCategoryChartProps {
  data?: IGetSummaryCategoryChart;
}

const IncomeCategoryChart: React.FC<IncomeCategoryChartProps> = ({ data }) => {
  const chartData = useMemo(() => {
    const defaultData = [
      { name: 'Services', value: 0, percentage: 0, color: '#4B63C7' },
      { name: 'Products', value: 0, percentage: 0, color: '#4384FF' },
      { name: 'Gift Cards', value: 0, percentage: 0, color: '#3AADD9' },
      { name: 'VIP', value: 0, percentage: 0, color: '#F4C04B' },
    ];

    if (!data) {
      return defaultData;
    }

    const total = data.services + data.products + data.giftCards + data.vips;

    if (total === 0) {
      return defaultData;
    }

    return [
      {
        name: 'Services',
        value: data.services,
        percentage: Math.round((data.services / total) * 100),
        color: '#4B63C7',
      },
      {
        name: 'Products',
        value: data.products,
        percentage: Math.round((data.products / total) * 100),
        color: '#4384FF',
      },
      {
        name: 'Gift Cards',
        value: data.giftCards,
        percentage: Math.round((data.giftCards / total) * 100),
        color: '#3AADD9',
      },
      {
        name: 'VIP',
        value: data.vips,
        percentage: Math.round((data.vips / total) * 100),
        color: '#F4C04B',
      },
    ];
  }, [data]);

  const allZeros = chartData.every((item) => item.value === 0);

  const displayData = useMemo(() => {
    if (allZeros) {
      return {
        labels: chartData.map((item) => item.name),
        datasets: [
          {
            data: chartData.map(() => 1),
            backgroundColor: chartData.map((item) => item.color),
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      };
    }

    return {
      labels: chartData.map((item) => item.name),
      datasets: [
        {
          data: chartData.map((item) => item.value),
          backgroundColor: chartData.map((item) => item.color),
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    };
  }, [chartData, allZeros]);

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.4,
    cutout: '60%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems: any) => {
            const item = chartData[tooltipItems[0].dataIndex];
            return `${item.name} (${item.percentage}%)`;
          },
          label: (tooltipItem: any) => {
            const item = chartData[tooltipItem.dataIndex];
            return `${formatCurrency(item.value)}`;
          },
        },
        titleFont: {
          size: 18,
          family: 'Poppins',
          weight: 'bold',
        },
        bodyFont: {
          size: 16,
          family: 'Poppins',
        },
        enabled: !allZeros,
      },
      datalabels: {
        display: !allZeros,
        color: '#fff',
        font: {
          weight: 'bold',
          size: 16,
        },
        formatter: (value: any, context: any) => {
          const item = chartData[context.dataIndex];
          return item.percentage > 0 ? `${item.percentage}%` : '';
        },
      },
    },
  };

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>Income</ChartTitle>
        <StatItem>
          <StatLabel>Total: </StatLabel>
          <StatValue>
            {formatCurrency(chartData?.reduce((sum, item) => sum + item.value, 0))}
          </StatValue>
        </StatItem>
      </ChartHeader>
      <Row wrap={false} justify='center' align='middle'>
        <ChartWrapper>
          <Doughnut
            data={displayData}
            //@ts-ignore
            options={options}
            plugins={[Tooltip, Legend, ChartDataLabels]}
          />
          {allZeros && <ZeroDataOverlay>Not Available</ZeroDataOverlay>}
        </ChartWrapper>

        <LegendContainer>
          {chartData.map((entry, index) => (
            <LegendItem key={`legend-item-${index}`}>
              <LegendName>
                <ColorIndicator color={entry.color} />
                <span>
                  {entry.name} ({entry.percentage}%)
                </span>
              </LegendName>
              <LegendValue>{formatCurrency(entry.value)}</LegendValue>
            </LegendItem>
          ))}
        </LegendContainer>
      </Row>
    </ChartContainer>
  );
};

export default IncomeCategoryChart;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.div`
  font-size: 26px;
  font-weight: 400;
  color: #1e293b;
  text-transform: capitalize;
`;

const ChartWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: start;
  justify-content: center;
  position: relative;
`;

const ZeroDataOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: 500;
  color: #64748b;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 4px;
  pointer-events: none;
`;

const LegendContainer = styled.div`
  margin-top: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
  padding-left: 20px;
  text-transform: capitalize;
`;

const LegendName = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  color: #1e293b;
`;

const ColorIndicator = styled.div<{ color: string }>`
  position: absolute;
  top: 16px;
  left: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${(props) => props.color};
`;

const LegendValue = styled.span`
  font-weight: bold;
  color: #1e293b;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StatValue = styled.span`
  color: #c84b31;
  font-family: Poppins;
  font-weight: 600;
  font-size: 26px;
  line-height: 100%;
  letter-spacing: 0%;
`;

const StatLabel = styled.span`
  font-size: 24px;
  color: #1e293b;
  margin-right: 5px;
  font-family: Poppins;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0%;
  text-transform: uppercase;
`;
